import { apiClient } from './client'

export interface ChatUser {
  id: number
  username: string
  display_name: string
  role: string
  team_id: number | null
}

export interface ChatAttachment {
  id: number
  filename: string
  content_type: string
  file_size: number
  created_at: string
}

export interface ChatMessage {
  id: number
  conversation_id: number
  sender_id: number
  sender_name: string
  body: string
  attachment: ChatAttachment | null
  is_deleted: boolean
  created_at: string
}

export interface ConversationMember {
  user: ChatUser
  role: 'member' | 'moderator'
  is_muted: boolean
  joined_at: string
}

export interface Conversation {
  id: number
  kind: 'direct' | 'group' | 'team'
  name: string
  display_name: string
  team_id: number | null
  is_archived: boolean
  created_by_id: number | null
  created_at: string
  updated_at: string
  members: ConversationMember[]
  last_message: ChatMessage | null
  unread_count: number
  is_muted: boolean
  my_role: 'member' | 'moderator' | null
  /** L'utilisateur courant peut-il supprimer définitivement ce fil ?
   *  (owner : tout ; admin : groupes.) */
  can_delete: boolean
  /** Date de suppression auto d'un groupe archivé (archived_at + 6 mois), ISO,
   *  sinon null. Le front affiche un bandeau sur les 15 derniers jours. */
  auto_delete_at: string | null
}

export const ChatAPI = {
  contacts: (): Promise<ChatUser[]> =>
    apiClient.get('/api/pexitalk/chat/contacts/').then((r) => r.data),

  conversations: (): Promise<Conversation[]> =>
    apiClient.get('/api/pexitalk/chat/conversations/').then((r) => r.data),

  conversation: (id: number): Promise<Conversation> =>
    apiClient.get(`/api/pexitalk/chat/conversations/${id}/`).then((r) => r.data),

  createDirect: (userId: number): Promise<Conversation> =>
    apiClient
      .post('/api/pexitalk/chat/conversations/', { kind: 'direct', user_id: userId })
      .then((r) => r.data),

  createGroup: (name: string, memberIds: number[]): Promise<Conversation> =>
    apiClient
      .post('/api/pexitalk/chat/conversations/', {
        kind: 'group',
        name,
        member_ids: memberIds,
      })
      .then((r) => r.data),

  messages: (
    conversationId: number,
    beforeId?: number,
  ): Promise<{ results: ChatMessage[]; has_more: boolean }> =>
    apiClient
      .get(`/api/pexitalk/chat/conversations/${conversationId}/messages/`, {
        params: beforeId ? { before_id: beforeId } : {},
      })
      .then((r) => r.data),

  sendMessage: (
    conversationId: number,
    body: string,
    attachmentId?: number,
  ): Promise<ChatMessage> =>
    apiClient
      .post(`/api/pexitalk/chat/conversations/${conversationId}/messages/`, {
        body,
        attachment_id: attachmentId,
      })
      .then((r) => r.data),

  deleteMessage: (messageId: number): Promise<void> =>
    apiClient.delete(`/api/pexitalk/chat/messages/${messageId}/`).then(() => undefined),

  /** Suppression DÉFINITIVE d'un fil (owner : tout ; admin : groupes). */
  deleteConversation: (conversationId: number): Promise<void> =>
    apiClient.delete(`/api/pexitalk/chat/conversations/${conversationId}/`).then(() => undefined),

  reportMessage: (messageId: number, reason: string) =>
    apiClient
      .post(`/api/pexitalk/chat/messages/${messageId}/report/`, { reason })
      .then((r) => r.data),

  markRead: (conversationId: number) =>
    apiClient.post(`/api/pexitalk/chat/conversations/${conversationId}/read/`),

  setArchived: (conversationId: number, archived: boolean) =>
    apiClient.post(`/api/pexitalk/chat/conversations/${conversationId}/archive/`, {
      archived,
    }),

  setMuted: (conversationId: number, muted: boolean) =>
    apiClient.post(`/api/pexitalk/chat/conversations/${conversationId}/mute/`, { muted }),

  addMembers: (conversationId: number, userIds: number[]) =>
    apiClient.post(`/api/pexitalk/chat/conversations/${conversationId}/members/`, {
      user_ids: userIds,
    }),

  removeMember: (conversationId: number, userId: number) =>
    apiClient.delete(
      `/api/pexitalk/chat/conversations/${conversationId}/members/${userId}/`,
    ),

  uploadAttachment: (conversationId: number, file: File): Promise<ChatAttachment> => {
    const form = new FormData()
    form.append('file', file)
    return apiClient
      .post(`/api/pexitalk/chat/conversations/${conversationId}/attachments/`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      })
      .then((r) => r.data)
  },

  attachmentDownloadUrl: (attachmentId: number): Promise<{ url: string; filename: string }> =>
    apiClient
      .get(`/api/pexitalk/chat/attachments/${attachmentId}/download/`)
      .then((r) => r.data),

  unread: (): Promise<{ total: number; by_conversation: Record<number, number> }> =>
    apiClient.get('/api/pexitalk/chat/unread/').then((r) => r.data),

  reports: (status?: string) =>
    apiClient
      .get('/api/pexitalk/chat/reports/', { params: status ? { status } : {} })
      .then((r) => r.data),

  reviewReport: (reportId: number, status: 'resolved' | 'dismissed') =>
    apiClient
      .post(`/api/pexitalk/chat/reports/${reportId}/review/`, { status })
      .then((r) => r.data),
}
